import { logger } from '../utils/logger';

// ── App state model ───────────────────────────────────────────────────────────
// All UI, AR events, and recovery paths are driven by this single state value.
// Add new states here; the exhaustiveness guard in renderApp.ts will surface
// any renderer gaps at compile time.

export type AppState =
  | 'INTRO'
  | 'PERMISSION_PREP'
  | 'PERMISSION_ERROR'
  | 'AR_STARTING'
  | 'SCANNING'
  | 'SCAN_GUIDANCE'
  | 'ANCHORED_AWAITING_AIM'
  | 'WHALE_VISIBLE'
  | 'INFO_MODAL_OPEN'
  | 'TRACKING_LOST'
  | 'LOAD_ERROR'
  | 'UNSUPPORTED';

// ── Internal state ────────────────────────────────────────────────────────────

let currentState: AppState = 'INTRO';

type StateListener = (state: AppState) => void;
const listeners: StateListener[] = [];

// ── Read ──────────────────────────────────────────────────────────────────────

export function getState(): AppState {
  return currentState;
}

// ── Subscribe / unsubscribe ───────────────────────────────────────────────────

export function subscribe(listener: StateListener): () => void {
  listeners.push(listener);
  return () => {
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

function notify(): void {
  for (const listener of listeners) {
    listener(currentState);
  }
}

// ── Core transition ───────────────────────────────────────────────────────────
// All state changes must go through here so transitions are always logged.

export function transition(next: AppState): void {
  if (currentState === next) return;
  logger.log(`State: ${currentState} → ${next}`);
  currentState = next;
  notify();
}

// ── Named transition helpers ──────────────────────────────────────────────────
// These make call-sites readable and serve as the authoritative list of valid
// state edges. Using raw transition() elsewhere is fine for one-off paths.

/** User tapped Begin Experience on the intro screen. */
export function beginExperience(): void {
  transition('PERMISSION_PREP');
}

/** All required permissions were granted. */
export function permissionsGranted(): void {
  transition('AR_STARTING');
}

/** A required permission was denied by the user or browser. */
export function permissionDenied(): void {
  transition('PERMISSION_ERROR');
}

/** AR session initialised and camera fade-in has begun. */
export function arStarted(): void {
  transition('SCANNING');
}

/** 60-second scan timer elapsed without anchor acquisition. */
export function scanTimedOut(): void {
  transition('SCAN_GUIDANCE');
}

/** Image target acquired; begin awaiting correct aim direction. */
export function anchorAcquired(): void {
  transition('ANCHORED_AWAITING_AIM');
}

/** Aim threshold met; reveal the whale. */
export function whaleRevealed(): void {
  transition('WHALE_VISIBLE');
}

/** User opened the Info modal. */
export function openInfoModal(): void {
  transition('INFO_MODAL_OPEN');
}

/** User closed the Info modal. */
export function closeInfoModal(): void {
  transition('WHALE_VISIBLE');
}

/** Image tracking lost after whale was placed. */
export function trackingLost(): void {
  transition('TRACKING_LOST');
}

/** Whale asset failed to load. */
export function whaleLoadFailed(): void {
  transition('LOAD_ERROR');
}

/** Device/browser does not support the required AR capabilities. */
export function deviceUnsupported(): void {
  transition('UNSUPPORTED');
}
